import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiEdit, FiPrinter, FiTrash2, FiRefreshCw } from 'react-icons/fi';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import api from '../services/api';
import Modal from '../components/Modal';
import { formatDateTimeNL } from '../utils/dateFormat';
import { getBarcodeSpec, renderBarcodeOnSvg } from '../utils/productLabelPrint';
import '../components/Button.css';
import '../styles/ProductLabelPrint.css';
import './ProductDetail.css';
import { ProductNaamMetKlasse, PRODUCT_KLASSE_META } from '../components/ProductKlasseIcon';

function stripHtmlToText(value) {
  const raw = String(value || '');
  if (!raw.trim()) return '';

  // Keep some structure before stripping tags.
  const withBreaks = raw
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<\/ul>/gi, '\n');

  if (typeof window !== 'undefined' && window.DOMParser) {
    try {
      const parser = new window.DOMParser();
      const doc = parser.parseFromString(withBreaks, 'text/html');
      return (doc.body.textContent || '')
        .replace(/\n{3,}/g, '\n\n')
        .replace(/[ \t]+\n/g, '\n')
        .trim();
    } catch (_) {}
  }

  return withBreaks
    .replace(/<[^>]*>/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function formatTransactionType(type) {
  switch (String(type || '').toLowerCase()) {
    case 'sale':
      return 'Verkoop';
    case 'refund':
      return 'Creditorder';
    case 'woo_return_restock':
      return 'Retour verwerkt';
    case 'woo_stock_change':
      return 'Voorraad gewijzigd in WooCommerce';
    case 'woo_order_stock_change':
      return 'Woo-order voorraadwijziging';
    case 'woo_sync':
      return 'WooCommerce synchronisatie';
    case 'manual_adjustment':
      return 'Handmatige aanpassing';
    default:
      return type || '—';
  }
}

function formatTransactionField(field) {
  switch (String(field || '').toLowerCase()) {
    case 'voorraad':
      return 'voorraad';
    case 'voorraad_onderweg':
      return 'voorraad onderweg';
    case 'in_productie':
      return 'in productie';
    default:
      return field || null;
  }
}

/** Toonlabel voor verzendtype (webshop / magazijn-advies): Standaard → DPD. */
function formatVerzendtypeLeverbelofteProduct(product) {
  if (!product) return '—';
  const vt = String(product.verzendtype_leverbelofte || '').trim().toLowerCase();
  if (vt === 'standaard') return 'Standaard (DPD)';
  if (vt === 'dhl') return 'DHL';
  if (vt === 'pallet') return 'Pallet';
  const legacy = String(product.standaard_vervoerder || '').trim().toLowerCase();
  if (legacy === 'dpd') return 'Standaard (DPD)';
  if (legacy === 'dhl') return 'DHL';
  return '—';
}

function verzendtypeFormValueFromProduct(product) {
  if (!product) return '';
  const vt = String(product.verzendtype_leverbelofte || '').trim().toLowerCase();
  if (['standaard', 'dhl', 'pallet'].includes(vt)) return vt;
  if (product.standaard_vervoerder === 'dhl') return 'dhl';
  if (product.standaard_vervoerder === 'dpd') return 'standaard';
  return '';
}

function formatTransactionSource(source) {
  switch (String(source || '').toLowerCase()) {
    case 'woo':
      return 'WooCommerce';
    case 'app':
      return 'Inkoopsoftware';
    default:
      return source || null;
  }
}

function formatTransactionReference(t) {
  if (!t) return null;
  if (t.reference_type === 'woocommerce_order') {
    return `Order ${t.reference_label || t.reference_id || ''}`.trim();
  }
  if (t.reference_label) return t.reference_label;
  if (t.reference_id) return t.reference_id;
  return null;
}

function filterVisibleTransactions(transactions) {
  const list = Array.isArray(transactions) ? transactions : [];
  if (list.length === 0) return [];
  // Toon: sale (Verkoop), refund (Creditorder), woo_stock_change (handmatig in Woo), manual_adjustment (Inkoop app).
  // Verberg: woo_order_stock_change (oude data – order-gedreven, we slaan die niet meer op), woo_sync, woo_reconciled.
  return list.filter((tx) => {
    const type = String(tx?.gebeurtenis_type || '').toLowerCase();
    return type !== 'woo_sync' && type !== 'woo_reconciled' && type !== 'woo_order_stock_change';
  });
}

const ProductDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [product, setProduct] = useState(null);
  const [leveranciers, setLeveranciers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [voorraadTransacties, setVoorraadTransacties] = useState([]);
  const [loadingTransacties, setLoadingTransacties] = useState(false);
  const [openOrderVoorraad, setOpenOrderVoorraad] = useState(null);
  const [openOrderVoorraadLoading, setOpenOrderVoorraadLoading] = useState(false);

  const [seizoensfactorData, setSeizoensfactorData] = useState(null);
  const [loadingSeizoensfactor, setLoadingSeizoensfactor] = useState(false);
  const [opslagData, setOpslagData] = useState(null);
  const [loadingOpslag, setLoadingOpslag] = useState(false);

  const [retourStats, setRetourStats] = useState(null);
  const [retourStatsLoading, setRetourStatsLoading] = useState(false);

  // Bundel / onderdelen
  const [onderdelen, setOnderdelen] = useState([]);
  const [loadingOnderdelen, setLoadingOnderdelen] = useState(false);
  const [onderdelenError, setOnderdelenError] = useState(null);
  const [bundelSearchQuery, setBundelSearchQuery] = useState('');
  const [bundelSearchResults, setBundelSearchResults] = useState([]);
  const [bundelSearching, setBundelSearching] = useState(false);
  const [savingOnderdeel, setSavingOnderdeel] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [printLabelReady, setPrintLabelReady] = useState(false);
  const labelSvgRef = useRef(null);
  const [formData, setFormData] = useState({
    naam: '',
    sku: '',
    ean: '',
    beschrijving: '',
    categorie: '',
    prijs: '',
    inkoopprijs: '',
    voorraad: '',
    voorraad_onderweg: '',
    in_productie: '',
    minimum_voorraad: '',
    min_inkoop_aantal: '30',
    cbm_per_stuk: '',
    inhoud_liter: '',
    uitfaseren: false,
    leverancier_id: '',
    verzendtype_leverbelofte: ''
  });

  const totals = useMemo(() => {
    const voorraad = parseInt(product?.voorraad, 10) || 0;
    const onderweg = parseInt(product?.voorraad_onderweg, 10) || 0;
    const prod = parseInt(product?.in_productie, 10) || 0;
    return { voorraad, onderweg, prod, totaal: voorraad + onderweg + prod };
  }, [product]);

  const descriptionText = useMemo(() => stripHtmlToText(product?.beschrijving), [product?.beschrijving]);
  const descriptionPreview = useMemo(() => {
    const text = descriptionText || '';
    if (text.length <= 360) return text;
    return `${text.slice(0, 360).trim()}...`;
  }, [descriptionText]);

  const visibleVoorraadTransacties = useMemo(
    () => filterVisibleTransactions(voorraadTransacties),
    [voorraadTransacties]
  );

  const loadProduct = async () => {
    if (!id) {
      setProduct(null);
      setLoadError('Ongeldig product-id in URL');
      setLoading(false);
      return;
    }

    const requestId = String(id);
    console.log('[ProductDetail] Loading product', { id: requestId });
    setLoading(true);
    setLoadError(null);
    setProduct(null);
    try {
      // Axios heeft een lange timeout, maar voor de UI willen we niet eindeloos "Laden..." zien.
      const UI_TIMEOUT_MS = 45000;
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error(`UI timeout: product ${requestId} laadt niet binnen ${UI_TIMEOUT_MS}ms`)), UI_TIMEOUT_MS);
      });

      const res = await Promise.race([
        api.get(`/producten/${id}`),
        timeoutPromise
      ]);
      setProduct(res.data);
      console.log('[ProductDetail] Product loaded', { id: requestId, name: res?.data?.naam });
    } catch (e) {
      setProduct(null);
      setLoadError(e.response?.data?.error || e.message || 'Product kon niet worden geladen');
      console.log('[ProductDetail] Load failed', { id: requestId, error: e?.message || e });
    } finally {
      setLoading(false);
    }
  };

  const fetchLeveranciers = async () => {
    try {
      const response = await api.get('/leveranciers');
      setLeveranciers(Array.isArray(response.data) ? response.data : []);
    } catch {
      setLeveranciers([]);
    }
  };

  const fetchVoorraadTransacties = async (productId) => {
    if (!productId) return;
    setLoadingTransacties(true);
    try {
      const txRes = await api.get(`/producten/${productId}/voorraad-transacties?limit=100`);
      setVoorraadTransacties(Array.isArray(txRes.data) ? txRes.data : []);
    } catch {
      setVoorraadTransacties([]);
    } finally {
      setLoadingTransacties(false);
    }
  };

  const fetchOpenOrderVoorraad = async (productId) => {
    if (!productId) return;
    setOpenOrderVoorraadLoading(true);
    try {
      const res = await api.get(`/producten/${productId}/open-order-voorraad`);
      setOpenOrderVoorraad(res.data || null);
    } catch {
      setOpenOrderVoorraad(null);
    } finally {
      setOpenOrderVoorraadLoading(false);
    }
  };

  const fetchSeizoensfactor = async (productId) => {
    if (!productId) return;
    setLoadingSeizoensfactor(true);
    setSeizoensfactorData(null);
    try {
      const sfRes = await api.get(`/producten/${productId}/seizoensfactor`);
      setSeizoensfactorData(sfRes.data);
    } catch {
      setSeizoensfactorData(null);
    } finally {
      setLoadingSeizoensfactor(false);
    }
  };

  const fetchOpslagKosten = async (productId) => {
    if (!productId) return;
    setLoadingOpslag(true);
    setOpslagData(null);
    try {
      const res = await api.get(`/producten/${productId}/opslag-kosten`);
      setOpslagData(res.data);
    } catch {
      setOpslagData(null);
    } finally {
      setLoadingOpslag(false);
    }
  };

  const fetchOnderdelen = async (productId) => {
    if (!productId) return;
    setLoadingOnderdelen(true);
    setOnderdelenError(null);
    try {
      const res = await api.get(`/producten/${productId}/onderdelen`);
      setOnderdelen(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setOnderdelen([]);
      setOnderdelenError(err.response?.data?.error || err.message || 'Kon onderdelen niet laden');
    } finally {
      setLoadingOnderdelen(false);
    }
  };

  useEffect(() => {
    fetchLeveranciers();
    loadProduct();
  }, [id]);

  useEffect(() => {
    if (!product?.id) return;
    fetchVoorraadTransacties(product.id);
    fetchOpenOrderVoorraad(product.id);
    fetchSeizoensfactor(product.id);
    fetchOpslagKosten(product.id);
    fetchOnderdelen(product.id);
  }, [product?.id]);

  useEffect(() => {
    if (!product?.id) {
      setRetourStats(null);
      return undefined;
    }
    let cancelled = false;
    setRetourStatsLoading(true);
    api
      .get(`/producten/${product.id}/retour-stats`)
      .then((res) => {
        if (!cancelled) setRetourStats(res.data || null);
      })
      .catch(() => {
        if (!cancelled) setRetourStats(null);
      })
      .finally(() => {
        if (!cancelled) setRetourStatsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [product?.id]);

  useEffect(() => {
    if (!printLabelReady || !product || !labelSvgRef.current) return undefined;
    const spec = getBarcodeSpec(product);
    if (!spec) {
      setPrintLabelReady(false);
      return undefined;
    }
    const ok = renderBarcodeOnSvg(labelSvgRef.current, spec);
    if (!ok) {
      setPrintLabelReady(false);
      window.alert('Label kon niet worden voorbereid (streepjescode). Controleer EAN/SKU of vernieuw de pagina.');
      return undefined;
    }

    const afterPrint = () => {
      setPrintLabelReady(false);
      window.removeEventListener('afterprint', afterPrint);
    };
    window.addEventListener('afterprint', afterPrint);
    const timer = window.setTimeout(() => window.print(), 120);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('afterprint', afterPrint);
    };
  }, [printLabelReady, product]);

  const handlePrintProductLabel = () => {
    if (!getBarcodeSpec(product)) {
      alert('Geen EAN of SKU om te coderen. Vul een van beide in bij product bewerken.');
      return;
    }
    setPrintLabelReady(true);
  };

  // Debounced product search voor het toevoegen van een onderdeel
  useEffect(() => {
    const q = bundelSearchQuery.trim();
    if (q.length < 2) {
      setBundelSearchResults([]);
      return undefined;
    }
    let cancelled = false;
    setBundelSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await api.get(`/producten/magazijn/zoek?q=${encodeURIComponent(q)}`);
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : [];
        const existingIds = new Set(onderdelen.map((o) => o.child_product_id));
        setBundelSearchResults(
          list.filter((p) => p.id !== product?.id && !existingIds.has(p.id)).slice(0, 12)
        );
      } catch {
        if (!cancelled) setBundelSearchResults([]);
      } finally {
        if (!cancelled) setBundelSearching(false);
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [bundelSearchQuery, onderdelen, product?.id]);

  const addOnderdeel = async (childProductId, aantal = 1) => {
    if (!product?.id) return;
    setSavingOnderdeel(true);
    try {
      await api.post(`/producten/${product.id}/onderdelen`, {
        child_product_id: childProductId,
        aantal
      });
      setBundelSearchQuery('');
      setBundelSearchResults([]);
      await fetchOnderdelen(product.id);
    } catch (err) {
      alert('Toevoegen mislukt: ' + (err.response?.data?.error || err.message));
    } finally {
      setSavingOnderdeel(false);
    }
  };

  const updateOnderdeelAantal = async (childProductId, aantal) => {
    if (!product?.id) return;
    const safe = Math.max(1, parseInt(aantal, 10) || 1);
    setOnderdelen((prev) =>
      prev.map((o) => (o.child_product_id === childProductId ? { ...o, aantal: safe } : o))
    );
    try {
      await api.put(`/producten/${product.id}/onderdelen/${childProductId}`, { aantal: safe });
    } catch (err) {
      alert('Bijwerken mislukt: ' + (err.response?.data?.error || err.message));
      await fetchOnderdelen(product.id);
    }
  };

  const removeOnderdeel = async (childProductId) => {
    if (!product?.id) return;
    if (!window.confirm('Dit onderdeel uit de bundel verwijderen?')) return;
    try {
      await api.delete(`/producten/${product.id}/onderdelen/${childProductId}`);
      await fetchOnderdelen(product.id);
    } catch (err) {
      alert('Verwijderen mislukt: ' + (err.response?.data?.error || err.message));
    }
  };

  const getBundelInkoopModus = (p) =>
    p?.bundel_inkoop_als_geheel === 1 ? 'geheel' : 'los';

  const setBundelInkoopModus = async (modus) => {
    if (!product?.id || getBundelInkoopModus(product) === modus) return;
    const geheel = modus === 'geheel';
    setProduct((prev) =>
      prev
        ? {
            ...prev,
            bundel_inkoop_als_geheel: geheel ? 1 : 0,
            niet_los_inkopen: geheel ? 0 : 1,
          }
        : prev
    );
    try {
      await Promise.all([
        api.patch(`/producten/${product.id}/bundel-inkoop-flag`, {
          bundel_inkoop_als_geheel: geheel,
        }),
        api.patch(`/producten/${product.id}/niet-los-inkopen`, {
          niet_los_inkopen: !geheel,
        }),
      ]);
    } catch (err) {
      alert('Opslaan mislukt: ' + (err.response?.data?.error || err.message));
      await loadProduct();
    }
  };

  const openEdit = () => {
    if (!product) return;
    setFormData({
      naam: product.naam || '',
      sku: product.sku || '',
      ean: product.ean || '',
      beschrijving: product.beschrijving || '',
      categorie: product.categorie || '',
      prijs: product.prijs || '',
      inkoopprijs: product.inkoopprijs || '',
      voorraad: product.voorraad ?? 0,
      voorraad_onderweg: product.voorraad_onderweg ?? 0,
      in_productie: product.in_productie ?? 0,
      minimum_voorraad: product.minimum_voorraad ?? 0,
      min_inkoop_aantal: product.min_inkoop_aantal ?? 30,
      cbm_per_stuk: product.cbm_per_stuk ?? '',
      inhoud_liter: product.inhoud_liter ?? '',
      uitfaseren: product.uitfaseren === 1,
      leverancier_id: product.leverancier_id || '',
      verzendtype_leverbelofte: verzendtypeFormValueFromProduct(product)
    });
    setShowEditModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!product?.id) return;
    setSaving(true);
    try {
      const payload = {
        ...product,
        ...formData,
        prijs: parseFloat(formData.prijs) || 0,
        inkoopprijs: formData.inkoopprijs === '' ? null : (parseFloat(formData.inkoopprijs) || null),
        voorraad: parseInt(formData.voorraad, 10) || 0,
        voorraad_onderweg: parseInt(formData.voorraad_onderweg, 10) || 0,
        in_productie: parseInt(formData.in_productie, 10) || 0,
        minimum_voorraad: parseInt(formData.minimum_voorraad, 10) || 0,
        min_inkoop_aantal: parseInt(formData.min_inkoop_aantal, 10) || 30,
        cbm_per_stuk: formData.cbm_per_stuk === '' ? null : (parseFloat(formData.cbm_per_stuk) || null),
        inhoud_liter:
          formData.inhoud_liter === '' ? null : (parseFloat(String(formData.inhoud_liter).replace(',', '.')) || null),
        uitfaseren: formData.uitfaseren ? 1 : 0,
        leverancier_id: formData.leverancier_id || null,
        verzendtype_leverbelofte: ['standaard', 'dhl', 'pallet'].includes(formData.verzendtype_leverbelofte)
          ? formData.verzendtype_leverbelofte
          : null,
        standaard_vervoerder:
          formData.verzendtype_leverbelofte === 'standaard'
            ? 'dpd'
            : formData.verzendtype_leverbelofte === 'dhl'
              ? 'dhl'
              : null
      };
      await api.put(`/producten/${product.id}`, payload);
      setShowEditModal(false);
      await loadProduct();
    } catch (err) {
      alert('Opslaan mislukt: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!product?.id) return;
    if (!window.confirm('Weet je zeker dat je dit product wilt verwijderen?')) return;
    try {
      await api.delete(`/producten/${product.id}`);
      navigate('/producten');
    } catch (err) {
      alert('Verwijderen mislukt: ' + (err.response?.data?.error || err.message));
    }
  };

  if (loading) {
    return <div className="loading">Laden...</div>;
  }

  if (!product) {
    return (
      <div className="product-detail">
        <div className="product-detail-header">
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/producten')}>
            <FiArrowLeft /> Terug
          </button>
        </div>
        <div className="product-detail-error">
          {loadError || 'Product niet gevonden'}
        </div>
      </div>
    );
  }

  const isLow = (parseInt(product.voorraad, 10) || 0) <= (parseInt(product.minimum_voorraad, 10) || 0);
  const canPrintLabel = Boolean(getBarcodeSpec(product));

  const fmtRetourPct = (p) =>
    p == null || Number.isNaN(p)
      ? '—'
      : `${Number(p).toLocaleString('nl-NL', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
  const fmtEuro = (n, digits = 2) =>
    n == null || Number.isNaN(n)
      ? '—'
      : `€ ${Number(n).toLocaleString('nl-NL', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
  const fmtEuro4 = (n) => fmtEuro(n, 4);
  const retourVanafIso = retourStats?.verkoop_meegenomen_vanaf || null;
  const retourVanafReden = retourStats?.verkoop_meegenomen_reden || null;
  const retourVanafFmt =
    retourVanafIso && /^\d{4}-\d{2}-\d{2}$/.test(String(retourVanafIso))
      ? new Date(`${retourVanafIso}T12:00:00`).toLocaleDateString('nl-NL', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })
      : null;

  return (
    <div className="product-detail">
      <div className="product-detail-main">
      <div className="product-detail-header">
        <button type="button" className="btn btn-secondary" onClick={() => navigate('/producten')}>
          <FiArrowLeft /> Terug
        </button>
        <div className="product-detail-header-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handlePrintProductLabel}
            disabled={!canPrintLabel}
            title={
              canPrintLabel
                ? 'Drukt een 10×15 cm label af (papierformaat instellen in het printvenster).'
                : 'Vul eerst EAN of SKU in.'
            }
          >
            <FiPrinter /> Label 10×15
          </button>
          <button type="button" className="btn btn-secondary" onClick={openEdit}>
            <FiEdit /> Bewerken
          </button>
          <button type="button" className="btn btn-secondary btn-danger-outline" onClick={handleDelete}>
            <FiTrash2 /> Verwijderen
          </button>
        </div>
      </div>

      <div className="product-detail-title">
        <h1 className="page-title">
          <ProductNaamMetKlasse productId={product.id} naam={product.naam} product_klasse={product.product_klasse} iconSize={22} />
        </h1>
        {product.product_klasse && PRODUCT_KLASSE_META[product.product_klasse] ? (
          <span className="chip" title={PRODUCT_KLASSE_META[product.product_klasse].label}>
            {PRODUCT_KLASSE_META[product.product_klasse].label}
          </span>
        ) : null}
        {isLow && <span className="product-detail-badge-low">Lage voorraad</span>}
      </div>

      <div className="product-detail-chips">
        {product.sku ? <span className="chip">SKU: {product.sku}</span> : null}
        {product.ean ? <span className="chip">EAN: {product.ean}</span> : null}
        {product.leverancier_naam ? <span className="chip">Leverancier: {product.leverancier_naam}</span> : null}
        {product.categorie ? <span className="chip">Categorie: {product.categorie}</span> : null}
        {product.uitfaseren === 1 ? <span className="chip chip-muted">Uitgefaseerd</span> : null}
      </div>

      <div className="product-detail-sections">
        <details className="accordion card">
          <summary className="accordion-summary">
            <span>Voorraad</span>
          </summary>
          <div className="accordion-body">
            <div className="grid-2">
              <div className="kv"><span>Voorraad</span><strong>{totals.voorraad}</strong></div>
              <div className="kv"><span>Onderweg</span><strong>{totals.onderweg}</strong></div>
              <div className="kv"><span>In productie</span><strong>{totals.prod}</strong></div>
              <div className="kv"><span>Totaal</span><strong>{totals.totaal}</strong></div>
              <div className="kv"><span>Minimum</span><strong>{parseInt(product.minimum_voorraad, 10) || 0}</strong></div>
              <div className="kv"><span>Min. inkoop</span><strong>{parseInt(product.min_inkoop_aantal, 10) || 30}</strong></div>
              <div className="kv" title="Stuks in WooCommerce orders met status processing">
                <span>In behandeling (webshop)</span>
                <strong>
                  {openOrderVoorraadLoading
                    ? '…'
                    : openOrderVoorraad?.in_behandeling ?? 0}
                  {!openOrderVoorraadLoading && (openOrderVoorraad?.orders_count ?? 0) > 0
                    ? ` (${openOrderVoorraad.orders_count} orders)`
                    : ''}
                </strong>
              </div>
              <div
                className="kv"
                title="Nog niet toewijsbaar via FIFO — ook als andere producten op dezelfde order wél op voorraad zijn"
              >
                <span>Backorders</span>
                <strong className={(openOrderVoorraad?.backorders ?? 0) > 0 ? 'product-detail-backorders-warn' : ''}>
                  {openOrderVoorraadLoading ? '…' : openOrderVoorraad?.backorders ?? 0}
                  {!openOrderVoorraadLoading && (openOrderVoorraad?.backorder_orders_count ?? 0) > 0
                    ? ` (${openOrderVoorraad.backorder_orders_count} orders)`
                    : ''}
                </strong>
              </div>
            </div>
            {!openOrderVoorraadLoading && (openOrderVoorraad?.in_behandeling ?? 0) > 0 ? (
              <p className="muted bundel-hint product-detail-open-order-hint">
                {(openOrderVoorraad?.backorders ?? 0) > 0
                  ? 'Backorders zijn stuks die na chronologische toewijzing nog tekort zijn. Overige stuks in “In behandeling” horen bij open orders (bijv. andere regels op dezelfde order).'
                  : 'Alle open stuks zijn toewijsbaar op basis van huidige Woo-voorraad en FIFO-volgorde.'}
              </p>
            ) : null}
          </div>
        </details>

        <details className="accordion card">
          <summary className="accordion-summary">
            <span>Prijs & logistiek</span>
          </summary>
          <div className="accordion-body">
            <div className="grid-2">
              <div className="kv"><span>Verkoopprijs</span><strong>€{parseFloat(product.prijs || 0).toFixed(2)}</strong></div>
              <div className="kv"><span>Inkoopprijs</span><strong>€{parseFloat(product.inkoopprijs || 0).toFixed(2)}</strong></div>
              <div className="kv"><span>CBM/stuk</span><strong>{product.cbm_per_stuk ? parseFloat(product.cbm_per_stuk).toFixed(3) : '—'}</strong></div>
              <div className="kv">
                <span>Inhoud (liter)</span>
                <strong>
                  {product.inhoud_liter
                    ? parseFloat(product.inhoud_liter).toFixed(2)
                    : product.cbm_per_stuk
                      ? (parseFloat(product.cbm_per_stuk) * 1000).toFixed(2)
                      : '—'}
                </strong>
              </div>
              <div className="kv" title="Zelfde betekenis als op tab Magazijn: eerst leverbelofte (verzendtype), anders legacy standaard vervoerder.">
                <span>Verzendtype (leverbelofte)</span>
                <strong>{formatVerzendtypeLeverbelofteProduct(product)}</strong>
              </div>
            </div>
            {product.beschrijving ? (
              <div className="product-detail-description">
                <h3>Beschrijving</h3>
                <p>{showFullDescription ? descriptionText : descriptionPreview}</p>
                {descriptionText.length > 360 ? (
                  <button
                    type="button"
                    className="product-detail-more-btn"
                    onClick={() => setShowFullDescription((v) => !v)}
                  >
                    {showFullDescription ? 'Minder tonen' : 'Lees meer'}
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </details>

        <details className="accordion card" open>
          <summary className="accordion-summary">
            <span>Opslagkosten — Heeren vs AG</span>
          </summary>
          <div className="accordion-body product-detail-opslag">
            {loadingOpslag ? (
              <div className="loading">Opslagkosten berekenen…</div>
            ) : !opslagData ? (
              <p className="tx-empty">Opslagkosten niet beschikbaar.</p>
            ) : !opslagData.heeft_volume ? (
              <>
                <p className="muted bundel-hint">
                  Vul <strong>inhoud (liter)</strong> of <strong>CBM/stuk</strong> in om LVB-tarieven te kunnen toepassen.
                </p>
                <Link to="/producten?tab=opslag" className="product-detail-opslag-link">
                  Tarieven beheren →
                </Link>
              </>
            ) : (
              <>
                <div className="grid-2 product-detail-opslag-kpis">
                  <div className="kv">
                    <span>Volume</span>
                    <strong>{opslagData.inhoud_liter != null ? `${opslagData.inhoud_liter} L` : '—'}</strong>
                  </div>
                  <div className="kv">
                    <span>Voorraad magazijn</span>
                    <strong>{opslagData.voorraad_magazijn ?? opslagData.voorraad ?? '—'}</strong>
                  </div>
                  {(opslagData.voorraad_onderweg > 0 || opslagData.in_productie > 0) && (
                    <div className="kv muted-kv" title="Niet meegeteld — liggen nog niet in het magazijn">
                      <span>Onderweg / in productie</span>
                      <strong>
                        {opslagData.voorraad_onderweg ?? 0} / {opslagData.in_productie ?? 0}
                      </strong>
                    </div>
                  )}
                  {opslagData.vergelijking?.goedkoopste && opslagData.vergelijking.goedkoopste !== 'gelijk' && (
                    <div className="kv product-detail-opslag-highlight">
                      <span>Lagere maandkosten</span>
                      <strong>
                        {opslagData.vergelijking.goedkoopste_naam ||
                          (opslagData.vergelijking.goedkoopste === 'tarief_1'
                            ? opslagData.tarief_1_naam
                            : opslagData.tarief_2_naam)}
                        {opslagData.vergelijking.verschil_maand != null
                          ? ` (−${fmtEuro(opslagData.vergelijking.verschil_maand)})`
                          : ''}
                      </strong>
                    </div>
                  )}
                </div>

                <h3 className="product-detail-opslag-subtitle">
                  {opslagData.tarief_1_naam || 'Heeren'}
                </h3>
                <div className="grid-2 product-detail-opslag-kpis">
                  <div className="kv">
                    <span>Tariefgroep</span>
                    <strong title={opslagData.tier_range || ''}>
                      {opslagData.tariefgroep || '—'}
                      {opslagData.tier_range ? ` (${opslagData.tier_range})` : ''}
                    </strong>
                  </div>
                  <div className="kv">
                    <span>Opslag / stuk / dag</span>
                    <strong>{fmtEuro4(opslagData.opslag_per_dag_per_stuk)}</strong>
                  </div>
                  <div className="kv">
                    <span>Opslag / dag (voorraad)</span>
                    <strong>{fmtEuro4(opslagData.opslag_per_dag_huidige_voorraad)}</strong>
                  </div>
                  <div className="kv">
                    <span>Opslag / maand (voorraad)</span>
                    <strong>{fmtEuro(opslagData.opslag_per_maand_huidige_voorraad)}</strong>
                  </div>
                </div>

                {opslagData.tarief_2 && (
                  <>
                    <h3 className="product-detail-opslag-subtitle">
                      {opslagData.tarief_2_naam || 'AG'}
                    </h3>
                    <div className="grid-2 product-detail-opslag-kpis">
                      <div className="kv">
                        <span>Tariefgroep</span>
                        <strong title={opslagData.tarief_2.tier_range || ''}>
                          {opslagData.tarief_2.tariefgroep || '—'}
                          {opslagData.tarief_2.tier_range ? ` (${opslagData.tarief_2.tier_range})` : ''}
                        </strong>
                      </div>
                      <div className="kv">
                        <span>Opslag / stuk / week</span>
                        <strong>{fmtEuro(opslagData.tarief_2.tarief_per_week)}</strong>
                      </div>
                      <div className="kv">
                        <span>Opslag / week (voorraad)</span>
                        <strong>{fmtEuro(opslagData.tarief_2.opslag_per_week_totaal)}</strong>
                      </div>
                      <div className="kv">
                        <span>Opslag / maand (voorraad)</span>
                        <strong>{fmtEuro(opslagData.tarief_2.opslag_per_maand_huidige_voorraad)}</strong>
                      </div>
                    </div>
                  </>
                )}

                <h3 className="product-detail-opslag-subtitle">Gemiddelde ligduur &amp; omloop</h3>
                <div className="grid-2 product-detail-opslag-kpis">
                  <div className="kv">
                    <span>Verkocht (12 mnd)</span>
                    <strong>{opslagData.totaal_verkocht_12m ?? 0}</strong>
                  </div>
                  <div className="kv">
                    <span>Omloopsnelheid</span>
                    <strong>
                      {opslagData.omloopsnelheid != null ? `${opslagData.omloopsnelheid}×/jr` : '—'}
                    </strong>
                  </div>
                  <div className="kv">
                    <span>Gem. ligduur op voorraad</span>
                    <strong>
                      {opslagData.gemiddelde_ligduur_dagen != null
                        ? `${opslagData.gemiddelde_ligduur_dagen} dagen`
                        : '—'}
                    </strong>
                  </div>
                  <div className="kv" title="Gebaseerd op werkelijke verkoop (12 mnd) of verwachte verkoop als er weinig historie is">
                    <span>Bron ligduur</span>
                    <strong>
                      {opslagData.ligduur_bron === 'verkoop_12_maanden'
                        ? 'Verkoop 12 mnd'
                        : opslagData.ligduur_bron === 'verwachte_verkoop'
                          ? 'Verwachte verkoop'
                          : '—'}
                    </strong>
                  </div>
                </div>

                <h3 className="product-detail-opslag-subtitle">
                  Gemiddelde opslag per stuk ({opslagData.tarief_1_naam || 'Heeren'})
                </h3>
                <div className="grid-2 product-detail-opslag-kpis product-detail-opslag-highlight">
                  <div className="kv">
                    <span>Opslag per verkochte stuk</span>
                    <strong className="product-detail-opslag-emphasis">
                      {fmtEuro4(opslagData.opslag_per_verkochte_stuk)}
                    </strong>
                  </div>
                  {opslagData.tarief_2?.opslag_per_verkochte_stuk != null && (
                    <div className="kv">
                      <span>{opslagData.tarief_2_naam || 'AG'} per verkochte stuk (equiv.)</span>
                      <strong>{fmtEuro4(opslagData.tarief_2.opslag_per_verkochte_stuk)}</strong>
                    </div>
                  )}
                  <div className="kv">
                    <span>% van inkoopprijs (Heeren)</span>
                    <strong>
                      {opslagData.opslag_pct_van_inkoop != null
                        ? `${opslagData.opslag_pct_van_inkoop}%`
                        : '—'}
                    </strong>
                  </div>
                  <div className="kv">
                    <span>{opslagData.tarief_1_naam || 'Heeren'} / jaar (huidige voorraad)</span>
                    <strong>{fmtEuro(opslagData.opslag_per_jaar_huidige_voorraad)}</strong>
                  </div>
                </div>

                <p className="muted bundel-hint product-detail-opslag-hint">
                  Vergelijking Heeren vs AG op dezelfde pickvoorraad. Alleen <strong>magazijnvoorraad</strong> telt mee.
                  Opslag per verkocht stuk ≈ tarief × gemiddelde ligduur (AG: weektarief ÷ 7).
                  {opslagData.korting_pct > 0 ? ` Heeren-korting: ${opslagData.korting_pct}%.` : ''}
                  {' '}
                  <Link to="/producten?tab=opslag">Tarieven beheren</Link>
                </p>
              </>
            )}
          </div>
        </details>

        <details className="accordion card">
          <summary className="accordion-summary">
            <span>Retouren</span>
          </summary>
          <div className="accordion-body">
            {retourStatsLoading ? (
              <p className="empty-state small">Retourstatistieken laden…</p>
            ) : retourStats == null ? (
              <p className="empty-state small">Kon retourstatistieken niet laden.</p>
            ) : (
              <>
                <p className="muted bundel-hint">
                  {retourVanafFmt ? (
                    <>
                      <strong>Verkocht</strong> is uit WooCommerce-import, gesplitst naar kanaal zoals op het dashboard. <strong>Percentages:</strong> Bol-retour t.o.v. verkocht op het bol.com-kanaal; overig-retour t.o.v. verkocht op overige kanalen; totaal-retour t.o.v. alle verkopen — allemaal vanaf{' '}
                      <strong>{retourVanafFmt}</strong>
                      {retourVanafReden === 'parameter'
                        ? ' (vaste startdatum via parameter retour_pct_verkoop_vanaf). '
                        : retourVanafReden === 'eerste_retour_systeem'
                          ? ' (automatisch: eerste niet-geweigerde retour in het systeem). '
                          : '. '}
                      Retouren tellen alleen mee als ze in die periode zijn <strong>aangemeld</strong>.
                    </>
                  ) : (
                    <>
                      Er is nog geen startdatum voor retourstatistieken (geen parameter en nog geen retour in de database): verkocht en percentages
                      gebruiken nog <strong>alle</strong> geïmporteerde Woo-verkoop als noemer — dat kan oude verkopen meenemen vóór retourtracking. Verkocht is wel per kanaal gesplitst (bol.com vs overig).
                    </>
                  )}{' '}
                  <strong>Via Bol.com</strong> (retour) hoort bij verkopen die in Woo als kanaal <strong>bol.com</strong> tellen (zelfde regels als het dashboard: o.a. lege/onbekende bron, geen Shopify-koppeling). <strong>Overig</strong> = andere kanalen (webshop, Shopify, enz.). Geweigerde retouren tellen niet mee.
                  Zelf een startdatum kiezen (aanbevolen bij nieuwe retourtracking): zie{' '}
                  <Link to="/parameters">Parameters</Link>, sleutel <strong>retour_pct_verkoop_vanaf</strong> (formaat YYYY-MM-DD).
                </p>
                <div className="grid-2">
                  <div
                    className="kv"
                    title="Verkochte stuks met Woo-kanaallabel bol.com (zelfde logica als Dashboard › herkomst)"
                  >
                    <span>Verkocht bol.com-kanaal</span>
                    <strong>{retourStats.verkocht_stuks_bol_kanaal ?? '—'}</strong>
                  </div>
                  <div
                    className="kv"
                    title="Verkochte stuks op andere kanalen (Shopify, direct, meta, …)"
                  >
                    <span>Verkocht overige kanalen</span>
                    <strong>{retourStats.verkocht_stuks_overig_kanaal ?? '—'}</strong>
                  </div>
                  <div
                    className="kv"
                    title={
                      retourVanafFmt
                        ? `Alle kanalen samen, verkopen vanaf ${retourVanafFmt}`
                        : 'Alle kanalen samen (alle historische Woo-import)'
                    }
                  >
                    <span>Verkocht totaal</span>
                    <strong>{retourStats.verkocht_stuks ?? '—'}</strong>
                  </div>
                  <div className="kv">
                    <span>Retour via Bol.com</span>
                    <strong
                      title={`${retourStats.retour_stuks_bol ?? 0} retourstuks; percentage t.o.v. verkocht op bol.com-kanaal (${retourStats.verkocht_stuks_bol_kanaal ?? 0} stuks)`}
                    >
                      {`${retourStats.retour_stuks_bol ?? 0} (${fmtRetourPct(retourStats.pct_retour_bol)})`}
                    </strong>
                  </div>
                  <div className="kv">
                    <span>Retour overig</span>
                    <strong
                      title={`${retourStats.retour_stuks_overig ?? 0} retourstuks; percentage t.o.v. verkocht op overige kanalen (${retourStats.verkocht_stuks_overig_kanaal ?? 0} stuks)`}
                    >
                      {`${retourStats.retour_stuks_overig ?? 0} (${fmtRetourPct(retourStats.pct_retour_overig)})`}
                    </strong>
                  </div>
                  <div className="kv">
                    <span>Totaal retour</span>
                    <strong
                      title={`Percentage t.o.v. totaal verkocht (${retourStats.verkocht_stuks ?? 0} stuks)`}
                    >
                      {`${retourStats.retour_stuks_totaal ?? 0} (${fmtRetourPct(retourStats.pct_retour_totaal)})`}
                    </strong>
                  </div>
                </div>
              </>
            )}
          </div>
        </details>

        <details className="accordion card">
          <summary className="accordion-summary">
            <span>
              Bundel / Onderdelen
              {onderdelen.length > 0 ? (
                <span className="bundel-count-badge"> · {onderdelen.length}</span>
              ) : null}
            </span>
          </summary>
          <div className="accordion-body">
            <p className="muted bundel-hint">
              Voeg onderdelen toe als dit hoofdproduct in werkelijkheid uit meerdere
              losse producten bestaat. Bij verkoop verschijnt het hoofdproduct in de
              picklijst met daaronder de te picken onderdelen.
            </p>

            <div className="bundel-inkoop-flags">
              <p className="bundel-inkoop-flags-title">
                <strong>Hoe bestel je bij de leverancier?</strong>
              </p>
              <label
                className={`bundel-inkoop-flag ${onderdelen.length === 0 ? 'is-disabled' : ''}`}
                title={onderdelen.length === 0 ? 'Voeg eerst onderdelen toe' : undefined}
              >
                <input
                  type="radio"
                  name={`bundel-inkoop-modus-${product?.id}`}
                  checked={getBundelInkoopModus(product) === 'geheel'}
                  onChange={() => setBundelInkoopModus('geheel')}
                  disabled={onderdelen.length === 0}
                />
                <span>
                  <strong>Set als geheel inkopen</strong>
                  <span className="muted bundel-inkoop-flag-hint">
                    {' '}Inkoopvoorstel op deze set (bijv. 10 sets). Onderdelen niet los. Zet op tafel/stoel/bank
                    ook &quot;Niet los inkopen&quot; aan.
                  </span>
                </span>
              </label>
              <label className="bundel-inkoop-flag">
                <input
                  type="radio"
                  name={`bundel-inkoop-modus-${product?.id}`}
                  checked={getBundelInkoopModus(product) === 'los'}
                  onChange={() => setBundelInkoopModus('los')}
                  disabled={onderdelen.length === 0}
                />
                <span>
                  <strong>Onderdelen los inkopen</strong>
                  <span className="muted bundel-inkoop-flag-hint">
                    {' '}Geen inkoopvoorstel op de set; tafel, stoel en bank elk apart op basis van verkopen.
                  </span>
                </span>
              </label>
            </div>

            {loadingOnderdelen ? (
              <div className="loading">Onderdelen laden…</div>
            ) : onderdelenError ? (
              <p className="tx-empty">{onderdelenError}</p>
            ) : onderdelen.length === 0 ? (
              <p className="tx-empty">Dit product heeft nog geen onderdelen (geen bundel).</p>
            ) : (
              <table className="bundel-table">
                <thead>
                  <tr>
                    <th>Onderdeel</th>
                    <th>SKU / EAN</th>
                    <th>Locatie</th>
                    <th className="col-num">Voorraad</th>
                    <th className="col-num">Aantal in bundel</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {onderdelen.map((o) => (
                    <tr key={o.child_product_id}>
                      <td>
                        <button
                          type="button"
                          className="link-button"
                          onClick={() => navigate(`/producten/${o.child_product_id}`)}
                        >
                          {o.child_naam || `Product #${o.child_product_id}`}
                        </button>
                      </td>
                      <td className="muted">{o.child_sku || o.child_ean || '—'}</td>
                      <td>{o.child_magazijn_locatie || '—'}</td>
                      <td className="col-num">{o.child_voorraad ?? '—'}</td>
                      <td className="col-num">
                        <input
                          type="number"
                          min="1"
                          value={o.aantal}
                          onChange={(e) => {
                            const v = e.target.value;
                            setOnderdelen((prev) =>
                              prev.map((x) =>
                                x.child_product_id === o.child_product_id ? { ...x, aantal: v } : x
                              )
                            );
                          }}
                          onBlur={(e) => updateOnderdeelAantal(o.child_product_id, e.target.value)}
                          className="bundel-aantal-input"
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-secondary btn-danger-outline btn-small"
                          onClick={() => removeOnderdeel(o.child_product_id)}
                        >
                          <FiTrash2 />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div className="bundel-add">
              <label className="bundel-add-label">Onderdeel toevoegen</label>
              <input
                type="text"
                value={bundelSearchQuery}
                onChange={(e) => setBundelSearchQuery(e.target.value)}
                placeholder="Zoek op naam, SKU of EAN…"
                className="bundel-add-input"
                disabled={savingOnderdeel}
              />
              {bundelSearching ? (
                <div className="muted bundel-add-status">Zoeken…</div>
              ) : bundelSearchResults.length > 0 ? (
                <ul className="bundel-search-results">
                  {bundelSearchResults.map((p) => (
                    <li key={p.id}>
                      <button
                        type="button"
                        className="bundel-search-result-btn"
                        disabled={savingOnderdeel}
                        onClick={() => addOnderdeel(p.id, 1)}
                      >
                        <span className="bundel-search-name">{p.naam}</span>
                        <span className="muted bundel-search-meta">
                          {p.sku ? `SKU ${p.sku}` : null}
                          {p.sku && p.ean ? ' · ' : ''}
                          {p.ean ? `EAN ${p.ean}` : null}
                          {p.magazijn_locatie ? ` · ${p.magazijn_locatie}` : ''}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : bundelSearchQuery.trim().length >= 2 ? (
                <div className="muted bundel-add-status">Geen resultaten</div>
              ) : null}
            </div>
          </div>
        </details>

        <details className="accordion card">
          <summary className="accordion-summary">
            <span>Verkoop &amp; seizoen</span>
          </summary>
          <div className="accordion-body product-detail-seizoen">
            {loadingSeizoensfactor ? (
              <div className="loading">Verkoop- en seizoensdata laden…</div>
            ) : !seizoensfactorData ? (
              <p className="tx-empty">Geen verkoop- of seizoensdata beschikbaar.</p>
            ) : (
              <>
                <div className="grid-2 product-detail-seizoen-kpis">
                  <div className="kv">
                    <span>Verkocht (12 mnd)</span>
                    <strong>{seizoensfactorData.totaal_verkocht ?? 0}</strong>
                  </div>
                  <div className="kv">
                    <span>Gem. / maand</span>
                    <strong>{seizoensfactorData.gemiddelde_per_maand != null ? seizoensfactorData.gemiddelde_per_maand : '—'}</strong>
                  </div>
                  <div className="kv">
                    <span>Seizoensfactor (nu)</span>
                    <strong>{seizoensfactorData.huidige_seizoensfactor != null ? seizoensfactorData.huidige_seizoensfactor : '—'}</strong>
                  </div>
                  <div className="kv">
                    <span>Verw. verkoop / maand</span>
                    <strong>{seizoensfactorData.verwachte_verkoop_per_maand != null ? seizoensfactorData.verwachte_verkoop_per_maand : '—'}</strong>
                  </div>
                  <div className="kv">
                    <span>Omloopsnelheid</span>
                    <strong>{seizoensfactorData.omloopsnelheid != null ? `${seizoensfactorData.omloopsnelheid}×/jr` : '—'}</strong>
                  </div>
                  <div className="kv">
                    <span>Maanden met data</span>
                    <strong>{seizoensfactorData.aantal_maanden_met_data ?? 0}</strong>
                  </div>
                </div>
                <p className="product-detail-seizoen-hint muted">
                  Seizoensfactor per maand: verhouding t.o.v. gemiddelde (0,5–2,0). Huidige maand gemarkeerd.
                </p>
                {Array.isArray(seizoensfactorData.seizoensfactor_per_maand) && seizoensfactorData.seizoensfactor_per_maand.length > 0 ? (
                  <>
                    <div className="product-detail-seizoen-chart-wrap">
                      <ResponsiveContainer width="100%" height={240} minWidth={280}>
                        <BarChart
                          data={seizoensfactorData.seizoensfactor_per_maand.map((m) => ({
                            ...m,
                            label: (m.maand_naam || m.maand || '').slice(0, 3)
                          }))}
                          margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                        >
                          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                          <YAxis allowDecimals={false} width={36} tick={{ fontSize: 11 }} />
                          <Tooltip
                            formatter={(value, name) => [value, name === 'verkoop' ? 'Stuks' : name]}
                            labelFormatter={(_, payload) => payload?.[0]?.payload?.maand_naam || ''}
                          />
                          <Bar dataKey="verkoop" name="verkoop" radius={[4, 4, 0, 0]}>
                            {seizoensfactorData.seizoensfactor_per_maand.map((entry, i) => (
                              <Cell key={i} fill={entry.is_huidige_maand ? '#2fb8a8' : '#3FDFCF'} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="product-detail-seizoen-table-wrap">
                      <table className="product-detail-seizoen-table">
                        <thead>
                          <tr>
                            <th>Maand</th>
                            <th className="col-num">Verkocht</th>
                            <th className="col-num">Factor</th>
                          </tr>
                        </thead>
                        <tbody>
                          {seizoensfactorData.seizoensfactor_per_maand.map((m) => (
                            <tr key={m.maand} className={m.is_huidige_maand ? 'seizoen-row-current' : ''}>
                              <td>{m.maand_naam || m.maand}</td>
                              <td className="col-num">{m.verkoop ?? 0}</td>
                              <td className="col-num">{m.factor != null ? Number(m.factor).toFixed(2) : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <p className="tx-empty">Nog geen maandverkopen in de laatste 12 maanden voor dit product.</p>
                )}
              </>
            )}
          </div>
        </details>

        <details className="accordion card">
          <summary className="accordion-summary">
            <span>Voorraadtransacties</span>
            <span className="accordion-summary-right">
              <button
                type="button"
                className="btn btn-secondary accordion-action"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); fetchVoorraadTransacties(product.id); }}
                disabled={loadingTransacties}
              >
                <FiRefreshCw /> {loadingTransacties ? 'Verversen…' : 'Ververs'}
              </button>
            </span>
          </summary>
          <div className="accordion-body">
            {loadingTransacties ? (
              <div className="loading">Transacties laden…</div>
            ) : (
              <ul className="tx-list">
                {visibleVoorraadTransacties.length === 0 ? (
                  <li className="tx-empty">Geen transacties gevonden</li>
                ) : (
                  visibleVoorraadTransacties.map((t) => {
                    const delta = t.delta != null ? parseInt(t.delta, 10) : null;
                    const deltaStr = delta == null ? '—' : (delta > 0 ? `+${delta}` : `${delta}`);
                    const dtStr = formatDateTimeNL(t.created_at);
                    const ref = formatTransactionReference(t);
                    const typeLabel = formatTransactionType(t.gebeurtenis_type);
                    const fieldLabel = formatTransactionField(t.veld);
                    const sourceLabel = formatTransactionSource(t.source);
                    return (
                      <li key={t.id} className="tx-item">
                        <div className="tx-top">
                          <span className="tx-date">{dtStr}</span>
                          <span className={`tx-delta ${delta != null && delta < 0 ? 'neg' : (delta != null && delta > 0 ? 'pos' : '')}`}>
                            {deltaStr}
                          </span>
                        </div>
                        <div className="tx-mid">
                          <strong>{typeLabel}</strong>
                          {fieldLabel ? <span className="tx-field"> · {fieldLabel}</span> : null}
                        </div>
                        <div className="tx-bottom">
                          <span>{sourceLabel || '—'}</span>
                          {ref ? <span> · {ref}</span> : null}
                          {t.actor ? <span> · {t.actor}</span> : null}
                        </div>
                      </li>
                    );
                  })
                )}
              </ul>
            )}
          </div>
        </details>
      </div>
      </div>

      <div className="product-label-sheet" aria-hidden="true">
        <div className="product-label-sheet-inner">
          <h2 className="product-label-title">{product.naam}</h2>
          <p className="product-label-code-line">
            {product.ean ? (
              <span className="product-label-ean">EAN: {product.ean}</span>
            ) : null}
            {product.ean && product.sku ? <br /> : null}
            {product.sku ? <span className="product-label-sku">SKU: {product.sku}</span> : null}
            {!product.ean && !product.sku ? <>—</> : null}
          </p>
          <svg ref={labelSvgRef} className="product-label-barcode" />
          <p className="product-label-tagline">100% handmade with love</p>
        </div>
      </div>

      <Modal
        show={showEditModal}
        onClose={() => !saving && setShowEditModal(false)}
        title="Product bewerken"
        large={true}
      >
        <form onSubmit={handleSave} className="form">
          <div className="form-group">
            <label>Naam *</label>
            <input type="text" value={formData.naam} onChange={(e) => setFormData({ ...formData, naam: e.target.value })} required />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>SKU</label>
              <input type="text" value={formData.sku} onChange={(e) => setFormData({ ...formData, sku: e.target.value })} />
            </div>
            <div className="form-group">
              <label>EAN</label>
              <input type="text" value={formData.ean} onChange={(e) => setFormData({ ...formData, ean: e.target.value })} />
            </div>
          </div>
          <div className="form-group">
            <label>Beschrijving</label>
            <textarea value={formData.beschrijving} onChange={(e) => setFormData({ ...formData, beschrijving: e.target.value })} rows="3" />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Categorie</label>
              <input type="text" value={formData.categorie} onChange={(e) => setFormData({ ...formData, categorie: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Verkoopprijs (€)</label>
              <input type="number" step="0.01" value={formData.prijs} onChange={(e) => setFormData({ ...formData, prijs: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Inkoopprijs (€)</label>
              <input type="number" step="0.01" value={formData.inkoopprijs} onChange={(e) => setFormData({ ...formData, inkoopprijs: e.target.value })} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Voorraad</label>
              <input type="number" value={formData.voorraad} onChange={(e) => setFormData({ ...formData, voorraad: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Onderweg</label>
              <input type="number" value={formData.voorraad_onderweg} onChange={(e) => setFormData({ ...formData, voorraad_onderweg: e.target.value })} />
            </div>
            <div className="form-group">
              <label>In productie</label>
              <input type="number" value={formData.in_productie} onChange={(e) => setFormData({ ...formData, in_productie: e.target.value })} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Minimum voorraad</label>
              <input type="number" value={formData.minimum_voorraad} onChange={(e) => setFormData({ ...formData, minimum_voorraad: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Min. inkoop</label>
              <input type="number" value={formData.min_inkoop_aantal} onChange={(e) => setFormData({ ...formData, min_inkoop_aantal: e.target.value })} />
            </div>
            <div className="form-group">
              <label>CBM/stuk</label>
              <input type="number" step="0.001" value={formData.cbm_per_stuk} onChange={(e) => setFormData({ ...formData, cbm_per_stuk: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Inhoud (liter)</label>
              <input type="number" step="0.01" value={formData.inhoud_liter} onChange={(e) => setFormData({ ...formData, inhoud_liter: e.target.value })} placeholder="Optioneel" />
            </div>
          </div>
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.uitfaseren}
                onChange={(e) => setFormData({ ...formData, uitfaseren: e.target.checked })}
                style={{ marginRight: '0.5rem' }}
              />
              Uitfaseren (product wordt niet meer besteld)
            </label>
          </div>
          <div className="form-group">
            <label>Leverancier</label>
            <select
              value={formData.leverancier_id}
              onChange={(e) => setFormData({ ...formData, leverancier_id: e.target.value })}
            >
              <option value="">Geen leverancier</option>
              {Array.isArray(leveranciers) && leveranciers.map((lev) => (
                <option key={lev.id} value={lev.id}>{lev.naam}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Verzendtype (leverbelofte) — magazijn-advies: Standaard = DPD</label>
            <select
              value={formData.verzendtype_leverbelofte}
              onChange={(e) => setFormData({ ...formData, verzendtype_leverbelofte: e.target.value })}
            >
              <option value="">Niet ingesteld</option>
              <option value="standaard">Standaard (DPD)</option>
              <option value="dhl">DHL</option>
              <option value="pallet">Pallet</option>
            </select>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)} disabled={saving}>
              Annuleren
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Opslaan…' : 'Opslaan'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ProductDetail;

